import { useState } from 'react'
import { Languages } from 'lucide-react'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card'
import { AvatarUpload } from '@/components/ui/AvatarUpload'
import { Avatar } from '@/components/ui/Avatar'
import { Button } from '@/components/ui/Button'
import { Input } from '@/components/ui/Input'
import { Label } from '@/components/ui/Label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectSeparator,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/Select'
import { RadioGroup, RadioGroupItem } from '@/components/ui/shadcn/radio-group'
import { TimezoneSelect } from '@/components/ui/TimezoneSelect'
import { UserProfile, Account, AvatarType, TimezoneOption } from './types'

interface PersonalInfoCardProps {
  user: UserProfile
  current_account: Account | null
  language_options: Array<[string, string]>
  timezone_options: TimezoneOption[]
  errors?: Record<string, string[]>
  onSubmit: (payload: { first_name: string; last_name: string; preferred_language: string; avatar_type: AvatarType; timezone: string | null }) => void
  onAvatarUpload: (file: File) => void
  isSubmitting?: boolean
}

export function PersonalInfoCard({
  user,
  current_account,
  language_options,
  timezone_options,
  errors,
  onSubmit,
  onAvatarUpload,
  isSubmitting = false,
}: PersonalInfoCardProps) {
  const [formData, setFormData] = useState({
    first_name: user.first_name || '',
    last_name: user.last_name || '',
    preferred_language: user.preferred_language || 'en',
    avatar_type: user.avatar_type || 'gravatar',
    timezone: user.timezone ?? '',
  })

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit({
      ...formData,
      timezone: formData.timezone || null,
    })
  }

  const handleLanguageChange = (value: string) => {
    if (!value) return
    setFormData({ ...formData, preferred_language: value })
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>Personal Information</CardTitle>
        <CardDescription>Update your account details and translation preferences</CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="flex flex-col gap-4 pb-6 border-b border-border md:flex-row md:items-start md:gap-6">
            {formData.avatar_type === 'custom' ? (
              <AvatarUpload
                currentUrl={user.avatar_url}
                initials={user.initials}
                onUpload={onAvatarUpload}
                size="lg"
                label="Change photo"
              />
            ) : (
              <Avatar
                name={user.full_name}
                email={user.email}
                avatarType={formData.avatar_type}
                size="xl"
              />
            )}
            <div className="flex-1 space-y-4">
              <div>
                <p className="font-medium">{user.full_name}</p>
                <p className="text-sm text-muted-foreground">{user.email}</p>
              </div>
              <div className="space-y-3">
                <Label>Avatar source</Label>
                <RadioGroup
                  value={formData.avatar_type}
                  onValueChange={(value) => setFormData({ ...formData, avatar_type: value as AvatarType })}
                  className="gap-3"
                >
                  <label className="flex items-center gap-3 cursor-pointer">
                    <RadioGroupItem value="gravatar" id="avatar-gravatar" />
                    <div>
                      <span className="text-sm font-medium">Gravatar</span>
                      <p className="text-xs text-muted-foreground">Uses your email to fetch avatar from gravatar.com</p>
                    </div>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <RadioGroupItem value="custom" id="avatar-custom" />
                    <div>
                      <span className="text-sm font-medium">Custom upload</span>
                      <p className="text-xs text-muted-foreground">Upload your own profile photo</p>
                    </div>
                  </label>
                  <label className="flex items-center gap-3 cursor-pointer">
                    <RadioGroupItem value="initials" id="avatar-initials" />
                    <div>
                      <span className="text-sm font-medium">Initials</span>
                      <p className="text-xs text-muted-foreground">Display your initials as avatar</p>
                    </div>
                  </label>
                </RadioGroup>
              </div>
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="first_name">First name</Label>
              <Input
                id="first_name"
                value={formData.first_name}
                onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
              />
              {errors?.first_name && (
                <p className="text-sm text-destructive">{errors.first_name[0]}</p>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="last_name">Last name</Label>
              <Input
                id="last_name"
                value={formData.last_name}
                onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
              />
              {errors?.last_name && (
                <p className="text-sm text-destructive">{errors.last_name[0]}</p>
              )}
            </div>
          </div>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Email</Label>
              <Input value={user.email} disabled className="bg-muted" />
              <p className="text-xs text-muted-foreground">Email cannot be changed</p>
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Label>Preferred language</Label>
                <Languages className="size-4 text-muted-foreground" />
              </div>
              <Select value={formData.preferred_language} onValueChange={handleLanguageChange}>
                <SelectTrigger className="cursor-pointer">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {language_options.map(([label, value], i) => (
                    value === ''
                      ? <SelectSeparator key={`sep-${i}`} />
                      : (
                        <SelectItem key={value} value={value} className="cursor-pointer">
                          {label}
                        </SelectItem>
                      )
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Messages from visitors will be auto-translated to this language.
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <Label>Timezone</Label>
            <TimezoneSelect
              value={formData.timezone}
              options={timezone_options}
              onChange={(value) => setFormData({ ...formData, timezone: value })}
              placeholder={`Use account default (${current_account?.timezone ?? user.effective_timezone})`}
            />
            <p className="text-xs text-muted-foreground">
              Used for date and time formatting throughout the app.{' '}
              {formData.timezone ? (
                <button
                  type="button"
                  className="underline cursor-pointer"
                  onClick={() => setFormData({ ...formData, timezone: '' })}
                >
                  Reset to account default ({current_account?.timezone ?? 'UTC'})
                </button>
              ) : (
                <>Inheriting from account ({user.effective_timezone}).</>
              )}
            </p>
            {errors?.timezone && (
              <p className="text-xs text-destructive">{errors.timezone.join(', ')}</p>
            )}
          </div>

          <div className="flex justify-end">
            <Button type="submit" disabled={isSubmitting} className="cursor-pointer">
              {isSubmitting ? 'Saving...' : 'Save changes'}
            </Button>
          </div>
        </form>
      </CardContent>
    </Card>
  )
}
